import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { ArrowLeft, CheckCircle2, ImageOff, BedDouble, Bath, Ruler, Layers, Home, Check, Info } from 'lucide-react';
import { mockSpecs, stagingStyles } from '../data/specsData';
import { initialModels, initialGlobalOptions } from '../data/optionsData';
export function SpecEditorPage() {
  const {
    builderId,
    specId
  } = useParams<{
    builderId: string;
    specId?: string;
  }>();
  const navigate = useNavigate();
  const isEditMode = !!specId;
  const existingSpec = useMemo(() => isEditMode ? mockSpecs.find(s => s.id === specId) : null, [specId, isEditMode]);
  // Form State
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [specName, setSpecName] = useState('');
  const [specDescription, setSpecDescription] = useState('');
  const [selectedStagingStyle, setSelectedStagingStyle] = useState<string | null>(null);
  // Map of variantSet -> optionId
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  // Initialize state for edit mode
  useEffect(() => {
    if (isEditMode && existingSpec) {
      setSelectedModelId(existingSpec.modelId);
      setSpecName(existingSpec.name);
      setSpecDescription(existingSpec.description);
      setSelectedStagingStyle(existingSpec.stagingStyleId);
      const initialSelections: Record<string, string> = {};
      existingSpec.selections.forEach(sel => {
        initialSelections[sel.variantSet] = sel.optionId;
      });
      setSelectedOptions(initialSelections);
    }
  }, [isEditMode, existingSpec]);
  const selectedModel = useMemo(() => initialModels.find(m => m.id === selectedModelId), [selectedModelId]);
  // Group options by Category -> VariantSet
  const groupedOptions = useMemo(() => {
    const groups: Record<string, Record<string, typeof initialGlobalOptions>> = {};
    initialGlobalOptions.forEach(opt => {
      if (!groups[opt.category]) {
        groups[opt.category] = {};
      }
      if (!groups[opt.category][opt.variantSet]) {
        groups[opt.category][opt.variantSet] = [];
      }
      groups[opt.category][opt.variantSet].push(opt);
    });
    return groups;
  }, []);
  const handleOptionToggle = (variantSet: string, optionId: string) => {
    setSelectedOptions(prev => {
      const next = {
        ...prev
      };
      if (next[variantSet] === optionId) {
        // Deselect if already selected
        delete next[variantSet];
      } else {
        // Select new option
        next[variantSet] = optionId;
      }
      return next;
    });
  };
  // Calculate totals
  const totals = useMemo(() => {
    let upgradeTotal = 0;
    const selectedDetails = [];
    Object.values(selectedOptions).forEach(optId => {
      const option = initialGlobalOptions.find(o => o.id === optId);
      if (option) {
        upgradeTotal += option.basePrice;
        selectedDetails.push(option);
      }
    });
    const basePrice = selectedModel?.basePrice || 0;
    return {
      basePrice,
      upgradeTotal,
      totalPrice: basePrice + upgradeTotal,
      selectedDetails
    };
  }, [selectedOptions, selectedModel]);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };
  const handleSave = (status: 'draft' | 'published') => {
    // In a real app, this would save to an API
    console.log('Saving spec:', {
      modelId: selectedModelId,
      name: specName,
      description: specDescription,
      status,
      stagingStyleId: selectedStagingStyle,
      selections: selectedOptions
    });
    navigate(`/${builderId}/specs`);
  };
  return <div className="space-y-6 max-w-7xl mx-auto pb-24" data-id="element-7411">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 sticky top-0 z-10 bg-slate-50/80 backdrop-blur-md py-4 border-b border-slate-200/50 -mx-4 px-4 sm:-mx-8 sm:px-8" data-id="element-7412">
        <div className="flex items-center gap-4" data-id="element-7413">
          <Button variant="ghost" onClick={() => navigate(isEditMode ? `/${builderId}/specs/${specId}` : `/${builderId}/specs`)} className="text-slate-500 hover:text-slate-900 px-2" data-id="element-7414">
            <ArrowLeft className="h-5 w-5" data-id="element-7415" />
          </Button>
          <div data-id="element-7416">
            <h1 className="text-2xl font-bold text-slate-900" data-id="element-7417">
              {isEditMode ? `Edit Spec: ${existingSpec?.name}` : 'Create New Spec'}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-7418">
          <Button variant="outline" onClick={() => navigate(`/${builderId}/specs`)} data-id="element-7419">
            Cancel
          </Button>
          <Button variant="outline" onClick={() => handleSave('draft')} disabled={!selectedModelId || !specName} data-id="element-7420">
            Save as Draft
          </Button>
          <Button onClick={() => handleSave('published')} disabled={!selectedModelId || !specName} data-id="element-7421">
            Save & Publish
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start" data-id="element-7422">
        {/* Left Column: Form */}
        <div className="lg:col-span-2 space-y-8" data-id="element-7423">
          {/* Step 1: Spec Details */}
          <section data-id="element-7424">
            <div className="flex items-center gap-2 mb-4" data-id="element-7425">
              <div className="h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold" data-id="element-7426">
                1
              </div>
              <h2 className="text-lg font-bold text-slate-900" data-id="element-7427">Spec Details</h2>
            </div>
            <Card className="p-5 space-y-4" data-id="element-7428">
              <div data-id="element-7429">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7430">
                  Spec Name <span className="text-red-500" data-id="element-7431">*</span>
                </label>
                <input type="text" value={specName} onChange={e => setSpecName(e.target.value)} placeholder="e.g. Premium Package, Modern Minimalist" className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" data-id="element-7432" />
              </div>
              <div data-id="element-7433">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-7434">
                  Description{' '}
                  <span className="text-slate-400 font-normal" data-id="element-7435">(Optional)</span>
                </label>
                <textarea value={specDescription} onChange={e => setSpecDescription(e.target.value)} placeholder="Describe what makes this configuration special..." rows={3} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" data-id="element-7436" />
              </div>
            </Card>
          </section>

          {/* Step 2: Model Selection */}
          <section data-id="element-7437">
            <div className="flex items-center gap-2 mb-4" data-id="element-7438">
              <div className="h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold" data-id="element-7439">
                2
              </div>
              <h2 className="text-lg font-bold text-slate-900" data-id="element-7440">Select Model</h2>
            </div>

            {isEditMode && selectedModel ? <Card className="p-4 flex items-center gap-4 bg-slate-50 border-slate-200" data-id="element-7441">
                <div className="h-16 w-24 rounded bg-slate-200 overflow-hidden flex-shrink-0" data-id="element-7442">
                  {selectedModel.hasImage && selectedModel.imageUrl ? <img src={selectedModel.imageUrl} alt={selectedModel.name} className="w-full h-full object-cover opacity-70" data-id="element-7443" /> : <div className="w-full h-full flex items-center justify-center text-slate-400" data-id="element-7444">
                      <Home className="h-6 w-6" data-id="element-7445" />
                    </div>}
                </div>
                <div data-id="element-7446">
                  <h3 className="font-bold text-slate-900" data-id="element-7447">
                    {selectedModel.name}
                  </h3>
                  <p className="text-sm text-slate-500" data-id="element-7448">
                    Model cannot be changed while editing a spec.
                  </p>
                </div>
              </Card> : <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7449">
                {initialModels.map(model => {
              const isSelected = selectedModelId === model.id;
              return <Card key={model.id} className={`cursor-pointer transition-all overflow-hidden ${isSelected ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/10' : 'hover:border-slate-300'}`} onClick={() => setSelectedModelId(model.id)} data-id="element-7450">
                      <div className="h-32 bg-slate-100 relative" data-id="element-7451">
                        {model.hasImage && model.imageUrl ? <img src={model.imageUrl} alt={model.name} className="w-full h-full object-cover" data-id="element-7452" /> : <div className="w-full h-full flex items-center justify-center text-slate-400" data-id="element-7453">
                            <ImageOff className="h-8 w-8" data-id="element-7454" />
                          </div>}
                        {isSelected && <div className="absolute top-2 right-2 h-6 w-6 bg-primary-500 rounded-full flex items-center justify-center text-white shadow-sm" data-id="element-7455">
                            <Check className="h-4 w-4" data-id="element-7456" />
                          </div>}
                      </div>
                      <div className="p-4" data-id="element-7457">
                        <div className="flex justify-between items-start mb-1" data-id="element-7458">
                          <h3 className="font-bold text-slate-900" data-id="element-7459">
                            {model.name}
                          </h3>
                          <span className="text-sm font-semibold text-slate-900" data-id="element-7460">
                            {formatCurrency(model.basePrice)}
                          </span>
                        </div>
                        <p className="text-xs text-slate-500 mb-3" data-id="element-7461">
                          {model.type}
                        </p>

                        <div className="flex items-center gap-3 text-xs text-slate-600" data-id="element-7462">
                          <span className="flex items-center gap-1" data-id="element-7463">
                            <Ruler className="h-3 w-3" data-id="element-7464" /> {model.sqft}
                          </span>
                          <span className="flex items-center gap-1" data-id="element-7465">
                            <BedDouble className="h-3 w-3" data-id="element-7466" /> {model.beds}
                          </span>
                          <span className="flex items-center gap-1" data-id="element-7467">
                            <Bath className="h-3 w-3" data-id="element-7468" /> {model.baths}
                          </span>
                        </div>
                      </div>
                    </Card>;
            })}
              </div>}
          </section>

          {/* Step 3: Staging Style */}
          <section className={!selectedModelId ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'} data-id="element-7469">
            <div className="flex items-center gap-2 mb-1" data-id="element-7470">
              <div className="h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold" data-id="element-7471">
                3
              </div>
              <h2 className="text-lg font-bold text-slate-900" data-id="element-7472">
                Staging Style
              </h2>
            </div>
            <p className="text-sm text-slate-500 mb-4 ml-8" data-id="element-7473">
              Choose a furniture and decor style for virtual staging and
              marketing renders
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-7474">
              {stagingStyles.map(style => {
              const isSelected = selectedStagingStyle === style.id;
              return <Card key={style.id} className={`cursor-pointer transition-all overflow-hidden ${isSelected ? 'ring-2 ring-primary-500 border-primary-500 bg-primary-50/10' : 'hover:border-slate-300'}`} onClick={() => setSelectedStagingStyle(isSelected ? null : style.id)} data-id="element-7475">
                    <div className="h-28 relative" data-id="element-7476">
                      <img src={style.thumbnailUrl} alt={style.name} className="w-full h-full object-cover" data-id="element-7477" />
                      {isSelected && <div className="absolute inset-0 bg-primary-500/20 flex items-center justify-center" data-id="element-7478">
                          <div className="h-8 w-8 bg-primary-500 rounded-full flex items-center justify-center text-white shadow-md" data-id="element-7479">
                            <Check className="h-5 w-5" data-id="element-7480" />
                          </div>
                        </div>}
                    </div>
                    <div className="p-4" data-id="element-7481">
                      <h3 className="font-bold text-slate-900 mb-1" data-id="element-7482">
                        {style.name}
                      </h3>
                      <p className="text-xs text-slate-500 line-clamp-2" data-id="element-7483">
                        {style.description}
                      </p>
                    </div>
                  </Card>;
            })}
            </div>
          </section>

          {/* Step 4: Configure Options */}
          <section className={!selectedModelId ? 'opacity-50 pointer-events-none transition-opacity' : 'transition-opacity'} data-id="element-7484">
            <div className="flex items-center gap-2 mb-4" data-id="element-7485">
              <div className="h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-bold" data-id="element-7486">
                4
              </div>
              <h2 className="text-lg font-bold text-slate-900" data-id="element-7487">
                Configure Options
              </h2>
            </div>

            <div className="space-y-8" data-id="element-7488">
              {Object.entries(groupedOptions).map(([category, variantSets]) => <div key={category} className="space-y-4" data-id="element-7489">
                  <h3 className="text-lg font-semibold text-slate-800 border-b border-slate-200 pb-2" data-id="element-7490">
                    {category}
                  </h3>

                  {Object.entries(variantSets).map(([variantSet, options]) => <Card key={variantSet} className="overflow-hidden" data-id="element-7491">
                      <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex justify-between items-center" data-id="element-7492">
                        <h4 className="font-medium text-slate-800" data-id="element-7493">
                          {variantSet}
                        </h4>
                        {selectedOptions[variantSet] && <span className="text-xs font-medium text-primary-600 flex items-center gap-1" data-id="element-7494">
                            <CheckCircle2 className="h-3.5 w-3.5" data-id="element-7495" /> Selected
                          </span>}
                      </div>
                      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-7496">
                        {options.map(option => {
                    const isSelected = selectedOptions[variantSet] === option.id;
                    return <div key={option.id} onClick={() => handleOptionToggle(variantSet, option.id)} className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all ${isSelected ? 'border-primary-500 bg-primary-50/30' : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'}`} data-id="element-7497">
                              {isSelected && <div className="absolute top-3 right-3 text-primary-500" data-id="element-7498">
                                  <CheckCircle2 className="h-5 w-5" data-id="element-7499" />
                                </div>}

                              <div className="flex gap-3" data-id="element-7500">
                                {option.hasImage && option.imageUrl && <div className="h-12 w-12 rounded bg-slate-100 flex-shrink-0 overflow-hidden" data-id="element-7501">
                                    <img src={option.imageUrl} alt={option.name} className="w-full h-full object-cover" data-id="element-7502" />
                                  </div>}
                                <div className="flex-1 pr-6" data-id="element-7503">
                                  <div className="flex items-start justify-between mb-1" data-id="element-7504">
                                    <h5 className={`font-semibold text-sm ${isSelected ? 'text-primary-900' : 'text-slate-900'}`} data-id="element-7505">
                                      {option.name}
                                    </h5>
                                  </div>

                                  <div className="flex items-center gap-2 mb-2" data-id="element-7506">
                                    {option.basePrice === 0 ? <span className="text-xs font-medium text-emerald-600" data-id="element-7507">
                                        Included
                                      </span> : <span className="text-xs font-semibold text-slate-900" data-id="element-7508">
                                        +{formatCurrency(option.basePrice)}
                                      </span>}
                                    {option.isStandard && <Badge variant="neutral" className="text-[10px] px-1.5 py-0" data-id="element-7509">
                                        Standard
                                      </Badge>}
                                  </div>

                                  <p className="text-xs text-slate-500 line-clamp-2" data-id="element-7510">
                                    {option.description}
                                  </p>
                                </div>
                              </div>
                            </div>;
                  })}
                      </div>
                    </Card>)}
                </div>)}
            </div>
          </section>
        </div>

        {/* Right Column: Sticky Summary */}
        <div className="lg:col-span-1" data-id="element-7511">
          <div className="sticky top-24 space-y-4" data-id="element-7512">
            <Card className="overflow-hidden shadow-sm border-slate-200" data-id="element-7513">
              <div className="bg-slate-900 p-5 text-white" data-id="element-7514">
                <h3 className="font-semibold text-slate-100 mb-1" data-id="element-7515">
                  Configuration Summary
                </h3>
                <p className="text-sm text-slate-400" data-id="element-7516">
                  {selectedModel ? selectedModel.name : 'No model selected'}
                </p>
              </div>

              <div className="p-5 space-y-4" data-id="element-7517">
                <div className="flex justify-between items-center text-sm" data-id="element-7518">
                  <span className="text-slate-500" data-id="element-7519">Base Price</span>
                  <span className="font-medium text-slate-900" data-id="element-7520">
                    {formatCurrency(totals.basePrice)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm" data-id="element-7521">
                  <span className="text-slate-500" data-id="element-7522">Total Upgrades</span>
                  <span className="font-medium text-primary-600" data-id="element-7523">
                    +{formatCurrency(totals.upgradeTotal)}
                  </span>
                </div>

                <div className="pt-4 border-t border-slate-100 flex justify-between items-center" data-id="element-7524">
                  <span className="font-bold text-slate-900" data-id="element-7525">Total Price</span>
                  <span className="text-xl font-bold text-slate-900" data-id="element-7526">
                    {formatCurrency(totals.totalPrice)}
                  </span>
                </div>
              </div>

              {totals.selectedDetails.length > 0 && <div className="border-t border-slate-100 bg-slate-50 p-5" data-id="element-7527">
                  <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3" data-id="element-7528">
                    Selected Options ({totals.selectedDetails.length})
                  </h4>
                  <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2" data-id="element-7529">
                    {totals.selectedDetails.map(opt => <div key={opt.id} className="flex justify-between items-start text-sm" data-id="element-7530">
                        <div className="pr-4" data-id="element-7531">
                          <p className="font-medium text-slate-900 text-xs" data-id="element-7532">
                            {opt.name}
                          </p>
                          <p className="text-[10px] text-slate-500" data-id="element-7533">
                            {opt.variantSet}
                          </p>
                        </div>
                        <span className="text-xs font-medium text-slate-600 whitespace-nowrap" data-id="element-7534">
                          {opt.basePrice === 0 ? 'Inc.' : `+${formatCurrency(opt.basePrice)}`}
                        </span>
                      </div>)}
                  </div>

                  {selectedStagingStyle && <div className="mt-4 pt-4 border-t border-slate-200" data-id="element-7535">
                      <p className="text-[10px] text-slate-500 mb-1" data-id="element-7536">
                        STAGING STYLE
                      </p>
                      <p className="font-medium text-slate-900 text-sm" data-id="element-7537">
                        {stagingStyles.find(s => s.id === selectedStagingStyle)?.name}
                      </p>
                    </div>}
                </div>}

              {!selectedModelId && <div className="p-4 bg-amber-50 border-t border-amber-100 flex items-start gap-3" data-id="element-7538">
                  <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-7539" />
                  <p className="text-sm text-amber-800" data-id="element-7540">
                    Please select a model to begin configuring your spec.
                  </p>
                </div>}
            </Card>
          </div>
        </div>
      </div>
    </div>;
}